import React, { useContext, useEffect, useState } from "react"
import Modal from "../../ui/Modal"
import { ReservationProps } from "../../../interfaces"
import Button from "../../ui/Button"
import { AdminContext } from "../../../contexts/AdminContext"
import classNames from "../../../utils/classNames"
import api from "../../../api2"
import CheckboxWithLabelForForm from "../../ui/CheckboxWithLabelForForm"
import Alert from "../../ui/Alert"
import MessageTypeSelector from "./MessageTypeSelector"
import { formatName } from "../../../utils/formatName"
import { TagsInput } from "react-tag-input-component"
import eSendingMode from "../../../enums/eSendingMode"
import { useCookies } from "react-cookie"
import { STATUSES } from "../../../constants"
import eSendingModeCookieName from "../../../enums/eSendingModeCookieName"
import IInvoice from "../../../interfaces/IInvoice"
import { useDebouncedCallback } from "use-debounce"
import CookieWrapper from "~/components/helpers/CookieWrapper"
import { ccCookie } from "~/constants/cookies"
import { formatPhoneNumber } from "~/utils"
import { useCamp } from "~/api2/hooks/useCamp"
import { useNotification } from "~/contexts/NotificationProvider"
import ICamper from "~/interfaces/ICamper"

const removeDuplicates = (array: any[]) => {
  return array.filter(
    (value, index, array) =>
      value != null && (typeof value === "string" ? value.trim() !== "" : true) && array.indexOf(value) === index,
  )
}

export interface MessageModalProps {
  open: boolean
  onClose: () => void
  reservations?: ReservationProps[]
  invoices?: IInvoice[]
  emails?: string[]
  ccEmails?: string[]
  phoneNumbers?: {
    countryCode?: string
    number: string
  }[]
  isBatch?: boolean
  isCCBlocked?: boolean
  isAttachmentsDisabled?: boolean
  sendNotification?: boolean
}

const cookieSendingMode = eSendingModeCookieName.MESSAGE

const MAX_MESSAGE_LENGTH = 480

const MessageModal = ({
  open,
  onClose,
  reservations,
  invoices,
  emails,
  ccEmails,
  phoneNumbers,
  isBatch,
  isCCBlocked,
  sendNotification,
  isAttachmentsDisabled,
}: MessageModalProps) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [isShowingCc, setIsShowingCc] = useState(false)
  const [isCcActive, setIsCcActive] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isIncludingReservation, setIsIncludingReservation] = useState(false)
  const [isIncludingInvoice, setIsIncludingInvoice] = useState(false)
  const [toEmails, setToEmails] = useState<string[]>([])
  const [toPhoneNumbers, setToPhoneNumbers] = useState<{ countryCode?: string; number: string }[]>([])
  const [displayPhoneNumbers, setDisplayPhoneNumbers] = useState<string[]>([])
  const [toCcEmails, setToCcEmails] = useState<string[]>([])
  const [messageContent, setMessageContent] = useState<string>("")
  const [isSendClicked, setIsSendClicked] = useState<boolean>(false)
  const [cookies, setCookie] = useCookies([cookieSendingMode])
  const { setIsMessageSent } = useContext(AdminContext)
  const { showNotification } = useNotification()

  const isValidSendingMode = Object.values(eSendingMode).includes(cookies[cookieSendingMode])
  const [selectedSendingMode, setSelectedSendingMode] = useState<eSendingMode>(
    isValidSendingMode ? cookies[cookieSendingMode] : eSendingMode.EMAIL,
  )

  const handleChangeSendingMode = (value: eSendingMode) => {
    setCookie(cookieSendingMode, value)
    setSelectedSendingMode(value)
  }

  const {
    index: { data: camp },
  } = useCamp()
  const emailValidation = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/
  const phoneValidation =
    /^(?=(?:.*[0-9]){6,})\+?([0-9]{1,4})?[-.\s]?(\()?([0-9]{1,3})?(\))?[-.\s]?([0-9]{1,4})[-.\s]?([0-9]{1,4})[-.\s]?([0-9]{1,9})$/

  const parsePhoneInput = (value: string) => {
    let normalized = value.trim()
    if (normalized.startsWith("00")) {
      normalized = `+${normalized.slice(2).trimStart()}`
    }
    const match = normalized.match(/^\+?(\d{1,4})?\s*(.*)$/)
    if (match) {
      return {
        countryCode: match[1] || undefined,
        number: match[2].replace(/\s+/g, ""),
      }
    }
    return { number: normalized.replace(/\s+/g, "") }
  }

  const formatPhoneForDisplay = (phone: { countryCode?: string; number: string }) => {
    if (!phone.number) return ""

    const countryCodeMap: Record<string, string> = {
      US: "+1",
      CA: "+1",
      AU: "+61",
      GB: "+44",
      NZ: "+64",
      MX: "+52",
      DE: "+49",
    }

    const campCallingCode = camp?.country ? countryCodeMap[camp?.country] : null
    const phoneCallingCode = phone.countryCode ? `+${phone.countryCode}` : ""

    const formattedWithCountryCode = formatPhoneNumber(phone.number, { countryCode: phone.countryCode })

    const shouldHideCountryCode = campCallingCode === phoneCallingCode

    if (shouldHideCountryCode && phone.countryCode) {
      return formattedWithCountryCode.replace(new RegExp(`^\\+${phone.countryCode}\\s`), "")
    } else {
      return formattedWithCountryCode
    }
  }

  useEffect(() => {
    const reservationPhoneNumbers =
      reservations
        ?.map((r) => {
          const camper = r.camper

          if (camper?.phone_number) {
            return {
              countryCode: camper.phone_country_code,
              number: camper.phone_number,
            }
          }

          return null
        })
        .filter(Boolean) || []

    const combinedPhoneNumbers = (phoneNumbers || [])
      .concat(reservationPhoneNumbers)
      .map((p) => parsePhoneInput(`${p.countryCode ? `+${p.countryCode} ` : ""}${p.number}`))
    const uniquePhoneNumbers = removeDuplicatesPhone(combinedPhoneNumbers)
    setToPhoneNumbers(removeDuplicates(combinedPhoneNumbers))
    setDisplayPhoneNumbers(uniquePhoneNumbers.map((phone) => formatPhoneForDisplay(phone)))
    const reservationEmails = reservations?.map((r) => r.camper?.email) || []
    const invoiceEmails = invoices?.map((i) => i.recipient_email) || []
    const combinedEmails = (emails || []).concat(reservationEmails).concat(invoiceEmails)
    setToEmails(removeDuplicates(combinedEmails))
    setToCcEmails(removeDuplicates(ccEmails || []))

    if (!open) {
      setIsProcessing(false)
      setIsShowingCc(false)
      setErrorMessage("")
      setIsIncludingReservation(false)
      setIsIncludingInvoice(false)
      setToEmails([])
      setToPhoneNumbers([])
      setDisplayPhoneNumbers([])
      setMessageContent("")
      setIsCcActive(false)
    }
  }, [open])

  const removeDuplicatesPhone = (phones: { countryCode?: string; number: string }[]) => {
    const seen = new Set()
    return phones.filter((phone) => {
      if (!phone || !phone.number || phone.number.trim() === "") return false

      const key = `${phone.countryCode || ""}${phone.number}`
      if (seen.has(key)) return false

      seen.add(key)
      return true
    })
  }

  const handleAddPhoneNumber = (phone: { countryCode?: string; number: string }) => {
    const sanitized = {
      countryCode: phone.countryCode?.replace(/^00/, ""),
      number: phone.number.replace(/^00/, ""),
    }
    const exists = toPhoneNumbers.some(
      (p) => (p.countryCode || "") + p.number === (sanitized.countryCode || "") + sanitized.number,
    )
    if (!exists) {
      const newPhones = [...toPhoneNumbers, sanitized]
      setToPhoneNumbers(newPhones)
      setDisplayPhoneNumbers(newPhones.map((p) => formatPhoneForDisplay(p)))
    }
  }

  const handleRemovePhoneNumber = (index: number) => {
    const updatedPhones = toPhoneNumbers.filter((_, i) => i !== index)
    setToPhoneNumbers(updatedPhones)
    setDisplayPhoneNumbers(updatedPhones.map((phone) => formatPhoneForDisplay(phone)))
  }

  const removeSentNumbersFromTo = (sentNumbers: string[]) => {
    if (Array.isArray(sentNumbers) && sentNumbers.length > 0) {
      const formattedSentNumbers = sentNumbers.map((num) =>
        typeof num === "string" ? (num.startsWith("+") ? num : `+${num}`) : num,
      )

      const newPhoneNumbers = [...toPhoneNumbers]

      const indicesToRemove: number[] = []
      newPhoneNumbers.forEach((phoneObj, index) => {
        const formattedPhone = `+${phoneObj.countryCode || ""}${phoneObj.number}`
        if (formattedSentNumbers.includes(formattedPhone)) {
          indicesToRemove.push(index)
        }
      })

      for (let i = indicesToRemove.length - 1; i >= 0; i--) {
        newPhoneNumbers.splice(indicesToRemove[i], 1)
      }

      setToPhoneNumbers(newPhoneNumbers)
      setDisplayPhoneNumbers(newPhoneNumbers.map((phone) => formatPhoneForDisplay(phone)))
    }
  }

  const debouncedSend = useDebouncedCallback((e) => {
    setErrorMessage("")
    setIsProcessing(true)

    const phoneStrings = toPhoneNumbers.map((phone) =>
      phone.countryCode ? `${phone.countryCode}${phone.number}` : phone.number,
    )

    api.Messages.sendEmail({
      to: selectedSendingMode == eSendingMode.EMAIL ? toEmails : phoneStrings,
      cc: toCcEmails.map((email) => email.trim()),
      from: e.target.from?.value,
      subject: e.target.subject?.value,
      text: messageContent,
      sending_mode: selectedSendingMode,
      admin_email: isCcActive ? camp?.email : null,
      reservation_id: reservations?.length === 1 ? reservations[0].id : null,
      reservation_ids: isBatch ? (reservations ? reservations.map((r) => r.id) : null) : null,
      invoice_ids: isBatch ? (invoices ? invoices.map((i) => i.id) : null) : null,
      is_including_invoice: isIncludingInvoice,
      is_including_reservation_details: isIncludingReservation,
      camp_id: camp?.id,
    })
      .then(() => {
        setIsProcessing(false)
        setIsMessageSent(true)
        onClose()
        setIsSendClicked(false)
        if (sendNotification) {
          showNotification({
            type: "success",
            title: "Message sent!",
            description: "Your message was successfully sent.",
          })
        }
      })
      .catch((err) => {
        if (err?.sent_recipients) {
          removeSentNumbersFromTo(err.sent_recipients)
        }
        setErrorMessage(err.message)
        setIsMessageSent(false)
        setIsProcessing(false)
        setIsSendClicked(false)
      })
  }, 100)

  const handleSend = (e: React.FormEvent<HTMLFormElement>) => {
    setIsSendClicked(true)
    e.preventDefault()
    debouncedSend(e)
  }

  if (!camp) return

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-3xl">
      <>
        <div className="flex items-center justify-between mb-4 sm:mb-4 bg-gray-100 px-4 pt-6 pb-4 -m-4 sm:-m-6 rounded-t-lg">
          <div className="flex flex-col w-full">
            <div className="font-semibold text-xl">
              {reservations?.length && reservations.length > 1
                ? "Message guests"
                : reservations?.length
                  ? `Message ${formatName(reservations[0].camper as ICamper) || "guest"}`
                  : `Message guest`}
            </div>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSend}>
          {errorMessage && <Alert description={errorMessage} />}

          <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4">
            <label htmlFor="from" className="text-baselg text-gray-500 mt-2.5">
              Type:
            </label>
            <div className="mt-2 sm:col-span-5 sm:mt-0 flex justify-start flex-col md:flex-row">
              <MessageTypeSelector
                options={[eSendingMode.EMAIL, eSendingMode.TEXT]}
                selectedOption={selectedSendingMode}
                onChange={handleChangeSendingMode}
              />
              {selectedSendingMode == eSendingMode.EMAIL && (
                <div className="flex items-center md:ml-3 mt-2 md:mt-0">
                  <CookieWrapper value={isCcActive} setValue={setIsCcActive} cookieName={ccCookie}>
                    <CheckboxWithLabelForForm
                      label={`cc ${camp?.email}`}
                      value={isCcActive}
                      setValue={setIsCcActive}
                      labelClasses="text-sm break-words w-64 truncate"
                      labelStyle={{ maxWidth: 243 }}
                    />
                  </CookieWrapper>
                </div>
              )}
            </div>
          </div>

          {selectedSendingMode == eSendingMode.EMAIL && (
            <>
              <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4">
                <label htmlFor="to" className="w-20 text-baselg text-gray-500 mt-2.5">
                  To:
                </label>
                <div className="mt-2 sm:col-span-5 sm:mt-0">
                  <div className="flex rounded-md shadow-sm">
                    <div className="relative flex items-stretch focus-within:z-10 w-full">
                      <div className="w-full">
                        <TagsInput
                          value={toEmails}
                          onChange={setToEmails}
                          onBlur={(e: any) => {
                            const value = e.target.value.trim()

                            if (value?.length && !toEmails.includes(value) && emailValidation.test(value)) {
                              setToEmails([...toEmails, value])
                              e.target.value = ""
                            }
                          }}
                          name="to"
                          separators={[",", "Enter", " ", "Tab"]}
                          beforeAddValidate={(value) => emailValidation.test(value) || toEmails.includes(value)}
                          classNames={{
                            tag: "",
                            input: "focus:outline-none focus:ring-0 focus:ring-offset-0",
                          }}
                          disabled={isProcessing}
                        />
                      </div>
                    </div>
                    {!isCCBlocked && (
                      <button
                        type="button"
                        className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-5 py-1 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-100"
                        onClick={() => setIsShowingCc(!isShowingCc)}
                      >
                        Cc
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {isShowingCc && (
                <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4">
                  <label htmlFor="cc" className="text-baselg text-gray-500 mt-2">
                    Cc:
                  </label>
                  <div className="mt-2 sm:col-span-5 sm:mt-0">
                    <TagsInput
                      value={toCcEmails}
                      onChange={setToCcEmails}
                      name="to"
                      separators={[",", "Enter", " ", "Tab"]}
                      beforeAddValidate={(value) => emailValidation.test(value) || toCcEmails.includes(value)}
                      classNames={{
                        tag: "",
                        input: "focus:outline-none focus:ring-0 focus:ring-offset-0",
                      }}
                      onBlur={(e: any) => {
                        const value = e.target.value.trim()

                        if (value?.length && !toCcEmails.includes(value) && emailValidation.test(value)) {
                          setToCcEmails([...toCcEmails, value])
                          e.target.value = ""
                        }
                      }}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
              )}
              <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4">
                <label htmlFor="from" className="text-baselg text-gray-500 mt-1.5">
                  From:
                </label>
                <div className="mt-2 sm:col-span-5 sm:mt-0">
                  <input
                    disabled={true}
                    type="email"
                    id="from"
                    name="from"
                    defaultValue={camp?.email}
                    required
                    className="cursor-not-allowed p-1 w-full border rounded-md opacity-60 border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 px-2"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4">
                <label htmlFor="subject" className="text-lg text-gray-500 mt-1">
                  Subject:
                </label>
                <div className="mt-2 sm:col-span-5 sm:mt-0">
                  <input
                    disabled={isProcessing}
                    type="text"
                    id="subject"
                    name="subject"
                    defaultValue={`Message from ${camp?.name}`}
                    required
                    className="p-1 w-full border rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 px-2"
                  />
                </div>
              </div>
            </>
          )}

          {selectedSendingMode === "text" && (
            <>
              <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4">
                <label htmlFor="to" className="w-20 text-baselg text-gray-500 mt-2.5">
                  To:
                </label>
                <div className="mt-2 sm:col-span-5 sm:mt-0">
                  <div className="flex rounded-md shadow-sm">
                    <div className="relative flex flex-grow items-stretch focus-within:z-10">
                      <div className="w-full">
                        <TagsInput
                          value={displayPhoneNumbers}
                          onChange={(tags) => {
                            tags.forEach((tag) => {
                              if (!displayPhoneNumbers.includes(tag)) {
                                handleAddPhoneNumber(parsePhoneInput(tag))
                              }
                            })
                            const removedIndices = displayPhoneNumbers
                              .map((val, i) => (tags.includes(val) ? null : i))
                              .filter((i) => i !== null) as number[]
                            if (removedIndices.length > 0) {
                              const newPhones = toPhoneNumbers.filter((_, i) => !removedIndices.includes(i))
                              setToPhoneNumbers(newPhones)
                              setDisplayPhoneNumbers(newPhones.map((phone) => formatPhoneForDisplay(phone)))
                            }
                          }}
                          name="to"
                          separators={[",", "Enter", "Tab"]}
                          beforeAddValidate={(value) => phoneValidation.test(value)}
                          classNames={{
                            tag: "",
                            input: "focus:outline-none focus:ring-0 focus:ring-offset-0",
                          }}
                          disabled={isProcessing}
                          onBlur={(e: any) => {
                            const value = e.target.value.trim()
                            if (value?.length && phoneValidation.test(value)) {
                              handleAddPhoneNumber(parsePhoneInput(value))
                              e.target.value = ""
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4">
            <label
              htmlFor="message"
              className={classNames(
                "w-20 text-lg",
                messageContent.length > MAX_MESSAGE_LENGTH && selectedSendingMode === "text"
                  ? "text-red-600"
                  : "text-gray-500",
              )}
            >
              Message:
            </label>
            <div className="mt-2 sm:col-span-5 sm:mt-0">
              <textarea
                rows={4}
                disabled={isProcessing}
                id="message"
                value={messageContent}
                onChange={(event) => {
                  setMessageContent(event.target.value)
                }}
                name="message"
                className={classNames(
                  "p-1 w-full border rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 px-2",
                  messageContent.length > MAX_MESSAGE_LENGTH &&
                    selectedSendingMode === "text" &&
                    "border-2 border-red-600 focus:border-red-600 focus:ring-red-600",
                )}
              />
              {messageContent.length > MAX_MESSAGE_LENGTH && selectedSendingMode === "text" && (
                <div className="text-red-600 font-semibold">
                  SMS body exceeds the {MAX_MESSAGE_LENGTH}-character limit ({messageContent.length} characters
                  provided)
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="flex space-x-2 justify-between">
              {reservations?.length > 0 && !isAttachmentsDisabled ? (
                <div className="flex flex-col md:flex-row md:gap-x-4">
                  <div className="flex items-center">
                    <CheckboxWithLabelForForm
                      label={
                        reservations?.some((reservation) => reservation.status === STATUSES.UNPAID)
                          ? "Add invoice"
                          : "Add receipt"
                      }
                      value={isIncludingInvoice}
                      setValue={setIsIncludingInvoice}
                      disabled={isProcessing || !reservations.some((r) => r?.order_id)}
                    />
                  </div>
                  <div className="flex items-center">
                    <CheckboxWithLabelForForm
                      label="Add reservation info"
                      value={isIncludingReservation}
                      setValue={setIsIncludingReservation}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
              ) : (
                <div />
              )}

              <div className="flex items-center">
                <div className="hidden sm:block">
                  <Button
                    type="button"
                    variant="gray"
                    className={classNames("mr-4 w-28", isProcessing && "pointer-events-none opacity-60")}
                    onClick={onClose}
                    disabled={isProcessing}
                  >
                    Cancel
                  </Button>
                </div>
                <Button
                  type="submit"
                  variant="green"
                  className="w-28"
                  disabled={
                    (!messageContent.trim() && !isIncludingInvoice && !isIncludingReservation) ||
                    (selectedSendingMode === eSendingMode.EMAIL && !toEmails.length) ||
                    (selectedSendingMode === eSendingMode.TEXT && !toPhoneNumbers.length) ||
                    isSendClicked ||
                    (messageContent.length > MAX_MESSAGE_LENGTH && selectedSendingMode === "text")
                  }
                >
                  Send
                </Button>
              </div>
            </div>
          </div>
        </form>
      </>
    </Modal>
  )
}

export default MessageModal
